/**
 * Record a substitute posting at a store — ad hoc install outside the campaign plan.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { TrackerLayout } from '@/pages/tracker/TrackerLayout';
import { SubstitutePostingBadge } from '@/components/admin/SubstitutePostingBadge';
import { RecoveryFormatQuantitiesTable } from '@/components/admin/RecoveryFormatQuantitiesTable';
import { getJson, http } from '@/lib/http';
import API_BASE from '@/lib/apiBase';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import type {
  SubstitutePostingCampaignPlan,
  SubstitutePostingReasonCode,
  SubstitutePostingStoreContext,
} from '@shared/substitute-posting-types';
import {
  ArrowLeft,
  Camera,
  Loader2,
  Upload,
  AlertTriangle,
} from 'lucide-react';

type ReasonCodeOption = { code: SubstitutePostingReasonCode; label: string };

function useQueryParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    cycleId: Number(params.get('cycleId')),
    storeId: Number(params.get('storeId')),
  };
}

export default function SubstitutePostingPage() {
  const { cycleId, storeId } = useQueryParams();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const scopeValid = Number.isFinite(cycleId) && cycleId > 0 && Number.isFinite(storeId) && storeId > 0;

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
  const [reasonCode, setReasonCode] = useState<SubstitutePostingReasonCode | ''>('');
  const [reasonNotes, setReasonNotes] = useState('');
  const [formatValues, setFormatValues] = useState<Record<number, number>>({});
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [completedLabel, setCompletedLabel] = useState<string | null>(null);

  const backHref = `/admin/activation-cockpit/progress/stores?cycleId=${cycleId}`;

  const { data: reasonCodesResponse } = useQuery<{ reasonCodes: ReasonCodeOption[] }>({
    queryKey: ['substitute-posting-reason-codes'],
    queryFn: () => getJson('/api/admin/substitute-postings/reason-codes'),
    enabled: scopeValid,
  });

  const { data: context, isLoading: contextLoading } = useQuery<SubstitutePostingStoreContext & { success: boolean }>({
    queryKey: ['substitute-posting-store-context', cycleId, storeId],
    queryFn: () => getJson(`/api/admin/substitute-postings/store-context?cycleId=${cycleId}&storeId=${storeId}`),
    enabled: scopeValid,
  });

  const campaignIdNum = selectedCampaignId ? Number(selectedCampaignId) : 0;

  const { data: planResponse, isLoading: planLoading } = useQuery<{ success: boolean; plan: SubstitutePostingCampaignPlan }>({
    queryKey: ['substitute-posting-campaign-plan', cycleId, storeId, campaignIdNum],
    queryFn: () => getJson(
      `/api/admin/substitute-postings/campaign-plan?cycleId=${cycleId}&storeId=${storeId}&campaignId=${campaignIdNum}`,
    ),
    enabled: scopeValid && campaignIdNum > 0,
  });

  const plan = planResponse?.plan;

  React.useEffect(() => {
    if (!plan) return;
    const next: Record<number, number> = {};
    for (const row of plan.formats) {
      next[row.formatId] = row.suggestedQuantity;
    }
    setFormatValues(next);
    setSelectedOperatorId(String(plan.operatorId));
  }, [plan]);

  const recordMutation = useMutation({
    mutationFn: async () => {
      const formats = Object.entries(formatValues)
        .map(([formatId, quantity]) => ({ formatId: Number(formatId), quantity: Number(quantity) }))
        .filter((row) => row.quantity > 0);

      const response = await apiRequest('/api/admin/substitute-postings/record', 'POST', {
        cycleId,
        storeId,
        campaignId: campaignIdNum,
        operatorId: Number(selectedOperatorId),
        reasonCode,
        reasonNotes: reasonNotes.trim() || null,
        formats,
      });
      return response.json();
    },
  });

  const uploadImages = useCallback(async (postingEventId: number) => {
    for (const file of pendingFiles) {
      const form = new FormData();
      form.append('file', file);
      const res = await http(
        `${API_BASE}/api/admin/substitute-postings/posting-events/${postingEventId}/evidence`,
        { method: 'POST', body: form },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || res.statusText);
      }
    }
  }, [pendingFiles]);

  const handleSubmit = async () => {
    if (!reasonCode || !campaignIdNum || !selectedOperatorId) {
      toast({ title: 'Missing fields', description: 'Select a campaign, operator, and reason.', variant: 'destructive' });
      return;
    }
    if (plan?.existingSubstituteLabel) {
      toast({
        title: 'Already recorded',
        description: plan.existingSubstituteLabel,
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await recordMutation.mutateAsync();
      const postingEventId = result.result?.postingEventId as number | undefined;
      const substituteLabel = result.result?.substituteLabel as string | undefined;

      if (postingEventId && pendingFiles.length > 0) {
        await uploadImages(postingEventId);
      }

      setCompletedLabel(substituteLabel ?? 'Substitute posting recorded');
      await queryClient.invalidateQueries({ queryKey: ['substitute-posting-store-context', cycleId, storeId] });
      toast({
        title: 'Substitute posting recorded',
        description: substituteLabel ?? 'Posting, confirmed delivery, and removal pipeline updated.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      const alreadyRecorded = message.includes('409:') || message.toLowerCase().includes('already recorded');
      if (alreadyRecorded) {
        const labelMatch = message.match(/:\s*(\[SUBSTITUTE POSTING\][^"]*)/);
        setCompletedLabel(labelMatch?.[1] ?? 'Substitute posting already recorded');
        toast({
          title: 'Substitute posting already recorded',
          description: 'This campaign already has a substitute posting at this store — likely from an earlier save attempt.',
        });
        return;
      }
      toast({
        title: 'Could not record substitute posting',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const formatRows = useMemo(() => {
    if (!plan) return [];
    return plan.formats.map((row) => ({
      formatId: row.formatId,
      formatName: row.formatName,
      planned: row.suggestedQuantity,
      operatorDelivered: null,
    }));
  }, [plan]);

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? [...event.target.files] : [];
    setPendingFiles((prev) => [...prev, ...files]);
    event.target.value = '';
  };

  if (!scopeValid) {
    return (
      <TrackerLayout title="Substitute posting">
        <Alert variant="destructive">
          <AlertTitle>Missing scope</AlertTitle>
          <AlertDescription>
            Open this page from Cockpit → By Store using cycleId and storeId.
          </AlertDescription>
        </Alert>
        <Button className="mt-4" variant="outline" onClick={() => setLocation('/admin/activation-cockpit/progress/stores')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to stores
        </Button>
      </TrackerLayout>
    );
  }

  if (completedLabel) {
    return (
      <TrackerLayout title="Substitute posting">
        <div className="max-w-2xl mx-auto space-y-4">
          <SubstitutePostingBadge label={completedLabel} />
          <Alert>
            <AlertTitle>Substitute posting saved</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{completedLabel}</p>
              <p className="text-sm text-muted-foreground">
                This posting is labelled as a substitute in tasks, proof of posting, and the locked cycle snapshot.
                Downstream removals will be generated automatically.
              </p>
            </AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => setLocation(backHref)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to stores
          </Button>
        </div>
      </TrackerLayout>
    );
  }

  return (
    <TrackerLayout title="Substitute posting">
      <div className="space-y-4 max-w-3xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => setLocation(backHref)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to stores
          </Button>
          <SubstitutePostingBadge />
        </div>

        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-900">Substitute posting</AlertTitle>
          <AlertDescription className="text-amber-900/90">
            Use when a campaign was physically posted at this store outside its planned store list
            (for example an aisle clash reallocation). The record will be clearly labelled as a substitute
            and will drive removals in the next cycle.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Store & cycle</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            {contextLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div><span className="text-muted-foreground">Store:</span> {context?.storeName}</div>
                <div><span className="text-muted-foreground">Cycle:</span> {context?.cycleName ?? context?.cycleId}</div>
                {(context?.substitutePostingCount ?? 0) > 0 && (
                  <div className="pt-2">
                    <SubstitutePostingBadge
                      label={`${context?.substitutePostingCount} existing at this store`}
                    />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Campaign</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Campaign in this cycle</Label>
              <Select value={selectedCampaignId || undefined} onValueChange={setSelectedCampaignId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent>
                  {(context?.campaigns ?? []).map((campaign) => (
                    <SelectItem
                      key={campaign.id}
                      value={String(campaign.id)}
                      disabled={campaign.alreadyRecordedAtStore}
                    >
                      {campaign.name}
                      {campaign.alreadyRecordedAtStore ? ' — substitute already recorded' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {plan?.existingSubstituteLabel && (
              <Alert variant="destructive">
                <AlertDescription>{plan.existingSubstituteLabel}</AlertDescription>
              </Alert>
            )}

            {planLoading && campaignIdNum > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading format plan…
              </div>
            )}

            {plan && !plan.existingSubstituteLabel && (
              <div className="space-y-2">
                <Label>Operator who installed</Label>
                <Select value={selectedOperatorId || undefined} onValueChange={setSelectedOperatorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {plan.availableOperators.map((operator) => (
                      <SelectItem key={operator.id} value={String(operator.id)}>
                        {operator.name}
                        {operator.id === plan.operatorId ? ' (suggested)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Defaults to the operator assigned to these formats at this store. Change if someone else did the install.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {plan && !plan.existingSubstituteLabel && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Formats posted</CardTitle>
              </CardHeader>
              <CardContent>
                <RecoveryFormatQuantitiesTable
                  formats={formatRows}
                  values={formatValues}
                  discrepancyReasons={{}}
                  onChange={(formatId, value) => setFormatValues((prev) => ({ ...prev, [formatId]: value }))}
                  onDiscrepancyReasonChange={() => {}}
                  deliveredLabel="Posted qty"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Reason</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Why was this a substitute posting?</Label>
                  <Select
                    value={reasonCode || undefined}
                    onValueChange={(value) => setReasonCode(value as SubstitutePostingReasonCode)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {(reasonCodesResponse?.reasonCodes ?? []).map((option) => (
                        <SelectItem key={option.code} value={option.code}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason-notes">Notes (optional)</Label>
                  <Textarea
                    id="reason-notes"
                    value={reasonNotes}
                    onChange={(e) => setReasonNotes(e.target.value)}
                    placeholder="e.g. Aisle clash with Brand X — posted here instead of store 441"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="h-4 w-4" /> Evidence photos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Add installation photos. They will be stored against the substitute posting proof record.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" asChild>
                    <label className="cursor-pointer">
                      <Upload className="h-4 w-4 mr-2" />
                      Add photos
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        className="sr-only"
                        onChange={onFileChange}
                      />
                    </label>
                  </Button>
                  {pendingFiles.length > 0 && (
                    <span className="text-sm text-muted-foreground self-center">
                      {pendingFiles.length} photo{pendingFiles.length === 1 ? '' : 's'} selected
                    </span>
                  )}
                </div>
                {pendingFiles.length > 0 && (
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {pendingFiles.map((file) => (
                      <li key={`${file.name}-${file.lastModified}`}>{file.name}</li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={recordMutation.isPending || !reasonCode || !selectedOperatorId}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {recordMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving substitute posting…
                  </>
                ) : (
                  'Record substitute posting'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </TrackerLayout>
  );
}
